import React, { useState, useEffect } from 'react';
import Masonry from 'react-masonry-css';
import { useInView } from 'react-intersection-observer';

import { IPressListFields } from '../../../types/contentful';

import { breakpoints } from '../../styles/media-queries';
import { ColorTokenProp } from '../../theme/tokens/color';

import { Text } from '../Text';
import { PressPost } from '../PressPost';
import { Button } from '../Button';
import { BlockContainer, Layout, BoxColumn } from '../Layout';

import { PressHeader, LoadMoreButtonWrapper, HiddenBox } from './styles';

import {
  MasonryWrapper,
  MASONRY_CLASS,
  MASONRY_COL_CLASS
} from '../MasonryWrapper';

const colors: ColorTokenProp[] = ['pressHeaderBg1', 'pressHeaderBg2'];
const NUMBER_TO_SHOW = 4;

export function PressList({ headingText, pressList }: IPressListFields) {
  const totalPressLength = pressList?.length || 0;

  const [isMobile, setIsMobile] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState<boolean | undefined>();
  const [focusedPostIndex, setFocusedPostIndex] = useState<
    number | undefined
  >();

  const getColor = (index: number) => {
    const colorPattern = isMobile
      ? colors
      : [...colors, ...[...colors].reverse()];
    return colorPattern[index % colorPattern.length];
  };

  const { ref } = useInView({
    triggerOnce: true
  });

  useEffect(() => {
    setIsMobile(
      typeof window !== `undefined` && window.innerWidth < breakpoints.tablet
    );
  }, []);

  useEffect(() => {
    setCanLoadMore((currentIndex + 1) * NUMBER_TO_SHOW < totalPressLength);
  }, [currentIndex]);

  return (
    <BlockContainer
      marginTop={{
        0: 0,
        tablet: 'l'
      }}
      marginBottom={{ 0: 'blockMarginY', tablet: 'blockMarginY_tablet' }}
      data-blockid="pressList"
      data-testid="press-list"
    >
      <Layout>
        <PressHeader size={10} paddingBottom={{ 0: 0 }}>
          <Text
            as="h2"
            fontWeight="h2"
            fontSize={{ 0: 'h2_0', tablet: 'h2_desktop' }}
          >
            {headingText}
          </Text>
        </PressHeader>
        <BoxColumn
          size={{ 0: 12, desktop: 10 }}
          paddingTop={{ 0: 0, desktop: 'xs' }}
        >
          {isMobile ? (
            (pressList || []).map(({ fields }, index) => (
              <HiddenBox
                key={index}
                firstPressItem={index === 0 ? true : false}
                hidden={index > (currentIndex + 1) * NUMBER_TO_SHOW - 1}
                data-testid={`press-post-${index}`}
                ref={ref}
              >
                <PressPost
                  {...fields}
                  color={getColor(index)}
                  focused={index === focusedPostIndex}
                />
              </HiddenBox>
            ))
          ) : (
            <MasonryWrapper>
              <Masonry
                breakpointCols={2}
                className={MASONRY_CLASS}
                columnClassName={MASONRY_COL_CLASS}
              >
                {(pressList || []).map(({ fields }, index) => (
                  <HiddenBox
                    key={index}
                    firstPressItem={index === 0 ? true : false}
                    hidden={index > (currentIndex + 1) * NUMBER_TO_SHOW - 1}
                    data-testid={`press-post-${index}`}
                    ref={ref}
                  >
                    <PressPost
                      {...fields}
                      color={getColor(index)}
                      focused={index === focusedPostIndex}
                    />
                  </HiddenBox>
                ))}
              </Masonry>
            </MasonryWrapper>
          )}

          {canLoadMore && (
            <LoadMoreButtonWrapper>
              <Button
                buttonColor="dark"
                onClick={() => {
                  const newIndex = currentIndex + 1;
                  setCurrentIndex(newIndex);
                  setFocusedPostIndex(newIndex * NUMBER_TO_SHOW);
                }}
              >
                Load more
              </Button>
            </LoadMoreButtonWrapper>
          )}
        </BoxColumn>
      </Layout>
    </BlockContainer>
  );
}
